import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Box, Typography, TextField, InputAdornment, IconButton, Alert, Tooltip } from "@mui/material";
import { AttachFile as AttachIcon, Send as SendIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import { WidgetContext } from "../../store/widgetContext";
import { axiosUCS } from "../../configs/http-config";
import Sender from "../Sender";
import Receiver from "../Receiver";
import TypingIndicator from "../TypingIndicator";
import AttachmentTray, { ALLOWED_MIME_TYPES, MAX_FILES, MAX_FILE_SIZE_BYTES } from "../Files/AttachmentTray";
import { FILE_STATUS } from "../Files/FileChip";
import { fileScanSSEClient } from "../../services/FileScanSSEClient";
import eventEmitter from "../../utils/eventEmitter";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { streamingManager } from "../../streaming";
import { useSession } from "../../session/useSession";
import { SessionManager } from "../../session/SessionManager";
import useSSEStreaming from "../../hooks/useSSEStreaming";
import { generateUUIDv4 } from "../../utils/contactUuid";

export default function Chat({ conversationId, initialAssignedAgents, handleNavigate, setSelectedDocument, setSelectedCollection, ...props }) {
	const { botId, tenantId, projectUUID } = props;
	const { widget, conversations, setConversations, contact, setContact, agents, contactDataCollector, setContactDataCollector } = useContext(WidgetContext);
	const { sessionId } = useSession();
	const inputRef = useRef();
	const pagePropsRef = useRef({ location: "chat", conversationId, assignedAgents: initialAssignedAgents || [] });
	const agentsRef = useRef(null);
	const fileInputRef = useRef(null);
	const messagesRef = useRef(null);
	const [query, setQuery] = useState("");
	const [initialQuery, setInitialQuery] = useState("");
	const [assignedAgentsState, setAssignedAgentsState] = useState([]);
	const [records, setRecords] = useState([]);
	const [conversation, setConversation] = useState(null);
	const [initialized, setInitialized] = useState(false);
	const initCalledRef = useRef(false);
	const [assistantTyping, setAssistantTyping] = useState(false);
	const [dataCollector, setDataCollector] = useState(null);
	
	const [fieldError, setFieldError] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [attachedFiles, setAttachedFiles] = useState([]);
	// Support/trace reference id state: `sendError` holds the id of a failed send so
	// the user can report it; `copiedRef` drives the "copied" tooltip feedback.
	const [sendError, setSendError] = useState(null);
	const [copiedRef, setCopiedRef] = useState(null);
	const currentConversation = conversations.find(convo => convo._id === pagePropsRef.current?.conversationId);
	const isResolved = !!(currentConversation?.status === "Resolved" && widget?.allow_resolved_convo_msg === false);
	const { msg, browserLang } = useWidgetMsg();
	useSSEStreaming({ conversationId, tenantId, botId });
	const streamSourceRef = useRef(new Map());

	const hasProcessingFiles = attachedFiles.some(f =>
		[FILE_STATUS.UPLOADING, FILE_STATUS.SCANNING].includes(f.scanStatus)
	);
	const readyFiles = attachedFiles.filter(f => f.scanStatus === FILE_STATUS.READY);
	const canSend = !isSending && !hasProcessingFiles && (query.trim() !== "" || readyFiles.length > 0);

	// --- File scan SSE ---
	useEffect(() => {
		const contactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
		if (!contactId || !tenantId) return;

		fileScanSSEClient.connect({ tenantId, contactId, botId });

		const unsubscribe = fileScanSSEClient.onStatusUpdate((data) => {
			const { fileId, scanStatus, scanVerdict } = data;
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === fileId ? { ...f, scanStatus, scanVerdict } : f)
			);
		});

		return () => {
			unsubscribe();
			fileScanSSEClient.disconnect();
		};
	}, [tenantId]);

	// --- File upload ---
	const uploadFile = useCallback(async (file) => {
		const contactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
		const formData = new FormData();
		formData.append("file", file);

		const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
		const newFileEntry = {
			fileId: tempId,
			originalName: file.name,
			sizeBytes: file.size,
			mimeType: file.type === "image/jpg" ? "image/jpeg" : file.type,
			scanStatus: FILE_STATUS.UPLOADING,
			previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
		};

		setAttachedFiles(prev => [...prev, newFileEntry]);

		try {
			const res = await axiosUCS.post("/files/upload", formData, {
				params: { tenantId, contactId, projectUUID, botId },
				headers: { "Content-Type": "multipart/form-data" },
			});

			const { fileId, scanStatus } = res.data?.data || res.data;
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === tempId ? { ...f, fileId, scanStatus: scanStatus || FILE_STATUS.SCANNING } : f)
			);
		} catch (error) {
			console.error("File upload failed:", error);
			setAttachedFiles(prev =>
				prev.map(f => f.fileId === tempId ? { ...f, scanStatus: FILE_STATUS.SCAN_FAILED } : f)
			);
		}
	}, [tenantId, projectUUID]);

	const handleFilesSelected = useCallback((files) => {
		const currentCount = attachedFiles.length;
		const remaining = MAX_FILES - currentCount;

		if (remaining <= 0) {
			setFieldError(msg("file.maxFilesReached", "Maximum {max} files allowed", { max: MAX_FILES }));
			return;
		}

		const filesToProcess = files.slice(0, remaining);

		for (const file of filesToProcess) {
			const normalizedType = file.type === "image/jpg" ? "image/jpeg" : file.type;
			if (!ALLOWED_MIME_TYPES.includes(normalizedType)) {
				setFieldError(msg("file.unsupportedType", "Unsupported file type: {name}", { name: file.name }));
				continue;
			}
			if (file.size > MAX_FILE_SIZE_BYTES) {
				setFieldError(msg("file.tooLarge", "File too large (max 2MB): {name}", { name: file.name }));
				continue;
			}
			setFieldError("");
			uploadFile(file);
		}
	}, [attachedFiles.length, uploadFile, msg]);

	const handleFileInputChange = useCallback((e) => {
		const files = Array.from(e.target.files);
		if (files.length > 0) handleFilesSelected(files);
		e.target.value = "";
	}, [handleFilesSelected]);

	const handleRemoveFile = useCallback((fileId) => {
		setAttachedFiles(prev => {
			const file = prev.find(f => f.fileId === fileId);
			if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
			return prev.filter(f => f.fileId !== fileId);
		});
	}, []);

	const handleDrop = useCallback((droppedFiles) => {
		handleFilesSelected(droppedFiles);
	}, [handleFilesSelected]);

	// --- Drag-and-drop on the chat area ---
	const [isDragOverChat, setIsDragOverChat] = useState(false);

	const handleChatDragOver = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isResolved && !dataCollector && !contactDataCollector) {
			setIsDragOverChat(true);
		}
	}, [isResolved, dataCollector, contactDataCollector]);

	const handleChatDragLeave = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOverChat(false);
	}, []);

	const handleChatDrop = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragOverChat(false);
		if (isResolved || dataCollector || contactDataCollector) return;
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) handleFilesSelected(files);
	}, [isResolved, dataCollector, contactDataCollector, handleFilesSelected]);

	// Copy a reference id to the clipboard and flash a short "copied" confirmation.
	const copyRef = useCallback((id) => {
		if (!id) return;
		navigator.clipboard?.writeText(id).catch(() => {
			// Stale embeds lack clipboard-write in the iframe allow list; fall back.
			const ta = document.createElement("textarea");
			ta.value = id;
			document.body.appendChild(ta);
			ta.select();
			try { document.execCommand("copy"); } catch (_) { /* nothing left to try */ }
			ta.remove();
		});
		setCopiedRef(id);
		setTimeout(() => setCopiedRef((prev) => (prev === id ? null : prev)), 1500);
	}, []);

	// --- Message handling ---
	const handleRequest = async (content, files) => {
		// Mint a per-message support/trace id. Sent as x-correlation-id so the
		// backend adopts it, persists it on the message doc, and propagates it to
		// every downstream service. Generated client-side (not read from the
		// response) so it is known even if the request times out with no reply.
		const correlationId = generateUUIDv4();
		try {
			const effectiveContactId = SessionManager.getSocketContactId();
			const metadata = SessionManager.getMetadata();
			const body = {
				uuid: projectUUID,
				contactId: effectiveContactId?.replace(/(_[^_]+)\1$/, '$1'),
				sessionId: sessionId?.replace(/(_[^_]+)\1$/, '$1'),
				metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
				content,
				tenantId,
				botId,
				lang: browserLang,
			};
			if (Array.isArray(files) && files.length > 0) {
				body.files = files;
			}
			await axiosUCS.post(`/widgets/query?botId=${botId}&tenantId=${tenantId}`, body, {
				headers: { "x-correlation-id": correlationId },
			});
			setSendError(null);
		} catch (error) {
			console.error("Error in handleRequest:", error);
			setSendError({ id: correlationId });
		}
	};

	const validateField = (collector, value) => {
		if (!collector || !collector.currAttr) return true;
		const type = collector.currAttr?.type?.toLowerCase();
		const field = collector.currAttr?.name;
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		if (type === "email" || field === "Email") {
			if (!emailPattern.test(value)) {
				return msg("help.invalid_email", "Please enter a valid email address");
			}
		}
		if (type === "number") {
			if (!/^\d+$/.test(value)) {
				return msg("help.invalid_number", "Please enter a valid number");
			}
		}
		return true;
	};

	const handleDataCollector = () => {
		if (!dataCollector) return false;
		const updatedAttrs = dataCollector.innerAttrs.map((attr) =>
			attr.field === dataCollector.currAttr?.field ? { ...attr, value: query.trim() } : attr
		);
		const allAttrsFilled = updatedAttrs.every((attr) => attr.value.trim() !== "");
		setDataCollector((prev) => ({
			...prev,
			innerAttrs: updatedAttrs,
			currAttr: allAttrsFilled ? null : prev.currAttr,
		}));
		setQuery("");
		return true;
	};

	const handleContactDataCollector = () => {
		if (!contactDataCollector) return false;
		const updatedAttrs = contactDataCollector.innerAttrs.map((attr) =>
			attr.field === contactDataCollector.currAttr?.field
				? { ...attr, value: query.trim() }
				: attr
		);
		const nextAttr = updatedAttrs.find((attr) => !attr.value);
		setQuery("");
		if (!nextAttr) {
			const { content } = contactDataCollector;
			setContactDataCollector(null);
			handleRequest({
				conversationId: pagePropsRef.current?.conversationId,
				action_query: {
					intent: content.config.intent,
					prevQuery: content?.flow === 6 ? null : initialQuery,
					nextWorkflowId: content?.config?.nextWorkflowId,
					meta: content,
					data: {
						attrs: updatedAttrs
					}
				},
			});
			setInitialQuery("");
		} else {
			setContactDataCollector((prev) => ({
				...prev,
				innerAttrs: updatedAttrs,
				currAttr: nextAttr,
			}));
		}
		return true;
	};

	const handleDefaultMessage = async () => {
		const conversationId = pagePropsRef.current?.conversationId;
		const fileIds = readyFiles.map(f => f.fileId);

		const content = {
			conversationId: conversationId || null,
			msg_query: { text: query },
		};

		await handleRequest(content, fileIds);
	};

	const handleSubmit = async () => {
		const input = query.trim();
		const hasReadyFiles = readyFiles.length > 0;

		if (!input && !hasReadyFiles) {
			setQuery("");
			return;
		}

		if (!canSend) return;

		setIsSending(true);

		if (input && !initialQuery && !contactDataCollector) {
			setInitialQuery(input);
		}

		if (input) {
			if (contactDataCollector) {
				const errorMessage = validateField(contactDataCollector, input);
				if (typeof errorMessage === "string") {
					setFieldError(errorMessage);
					setIsSending(false);
					return;
				}
			}
			if (dataCollector) {
				const errorMessage = validateField(dataCollector, input);
				if (typeof errorMessage === "string") {
					setFieldError(errorMessage);
					setIsSending(false);
					return;
				}
			}
			setFieldError("");
		}

		try {
			if (input && !hasReadyFiles) {
				if (handleDataCollector()) { setIsSending(false); return; }
				if (handleContactDataCollector()) { setIsSending(false); return; }
			}
			await handleDefaultMessage();
			setQuery("");
			setAttachedFiles(prev => {
				prev.forEach(f => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
				return [];
			});
		} catch (error) {
			console.error("Error in handleSubmit:", error);
		} finally {
			setIsSending(false);
		}
	};

	const initSessionAndGetContact = async (_records) => {
		if (_records.length === 0 && !initCalledRef.current) {
			initCalledRef.current = true;
			await handleRequest({
				conversationId: pagePropsRef.current?.conversationId || null,
				new_session: true,
			});
		}
	};

	const handleAssistantTyping = (message) => {
		const { conversationId } = message;
		if (pagePropsRef.current?.conversationId === conversationId) {
			setAssistantTyping(true);
		}
	};

	const updateCollectorState = (reply, setCollector, record) => {
		if (!reply) return;
		const attrs = reply?.config?.attrs || reply.attrs;
		if (!Array.isArray(attrs) || attrs.length === 0) return;
		setCollector((prev) => {
			const innerAttrs = attrs.map((attr) => ({ ...attr, value: attr?.value || "" }));
			const currentAttr = innerAttrs.find((attr) => !attr.value);
			return {
				...prev,
				content: reply,
				innerAttrs,
				currAttr: currentAttr,
				recordId: record._id,
			};
		});
	};

	const isValidRecord = (rec) => {
		if (rec.query?.type === "msg") return true;
		if (rec.reply?.type === "contact_data_collector") return true;
		if (["ticket_data_collector", "connect_data_collector"].includes(rec.reply?.type) && Array.isArray(rec.reply?.config?.attrs)) {
			const hasEmptyAttr = rec.reply.config.attrs.some(attr => !attr.value);
			if (!hasEmptyAttr) return false;
		}
		if (!rec.reply && !rec.query?.config?.message) {
			return false;
		}
		return true;
	};

	function updateConversationRecords(prev, id, updater) {
		const exists = prev.some(conv => conv._id === id);
		if (!exists) {
			return [...prev, {
				_id: id,
				records: updater([]).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
				placeholder: false,
				status: "Open",
			}];
		}
		return prev.map(conv => {
			if (conv._id !== id) return conv;
			return {
				...conv,
				records: updater(conv.records || []).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
			};
		});
	}

	function upsertRecord(records, newRecord) {
		const index = records.findIndex(r => r._id === newRecord._id);
		if (index === -1) {
			return [...records, newRecord];
		}
		return records.map(r => (r._id === newRecord._id ? newRecord : r));
	}

	function updateRecordReply(records, recordId, replyUpdater) {
		const exists = records.some(r => r._id === recordId);
		if (!exists) {
			return [
				...records,
				{
					_id: recordId,
					query: null,
					reply: replyUpdater({}),
				},
			];
		}
		return records.map(r =>
			r._id === recordId
				? { ...r, reply: replyUpdater(r.reply || {}) }
				: r
		);
	}

	const handleRecordUpdate = (message) => {
		const { contactId, record } = message;
		const fullContactId = SessionManager.getSocketContactId();
		if (contactId !== fullContactId) return;
		const stream = streamingManager.get(record._id);
		let finalRecord = record;
		if (stream?.text) {
			finalRecord = {
				...record,
				reply: {
					...(record.reply || {}),
					message: stream.text,
					streaming: false,
				},
			};
			streamingManager.remove(record._id);
		}
		setConversations(prev =>
			updateConversationRecords(prev, finalRecord.conversationId, records =>
				upsertRecord(records.filter(isValidRecord), finalRecord)
			)
		);
		if (!pagePropsRef.current?.conversationId && finalRecord.conversationId) {
			pagePropsRef.current = { ...pagePropsRef.current, conversationId: finalRecord.conversationId };
			if (handleNavigate) {
				handleNavigate({ conversationId: finalRecord.conversationId, assignedAgents: assignedAgentsState });
			}
		}
		if (["ticket_data_collector", "connect_data_collector"].includes(finalRecord.reply?.type)) {
			updateCollectorState(finalRecord.reply, setDataCollector, finalRecord);
		}
		if (finalRecord.reply?.type === "contact_data_collector") {
			updateCollectorState(finalRecord.reply, setContactDataCollector, finalRecord);
		}
		setAssistantTyping(false);
	};

	const handleRecordUpdateStream = (message) => {
		const { recordId, conversationId, chunk, done, fullMessage, agentIDaasId, _source } = message;
		if (!recordId || conversationId !== pagePropsRef.current?.conversationId) return;

		const claimed = streamSourceRef.current.get(recordId);
		if (claimed && claimed !== _source) return;
		if (!claimed && _source) streamSourceRef.current.set(recordId, _source);

		const prevState = streamingManager.get(recordId);
		const streamState = streamingManager.ingest({ recordId, conversationId, chunk, done });
		if (!prevState?.started && streamState.started) {
			setAssistantTyping(false);
		}
		if (!streamState.started) return;
		let finalText = streamState.text;
		if (done && fullMessage) {
			try { finalText = JSON.parse(fullMessage).response || streamState.text; } catch (_) { }
		}
		setConversations(prev =>
			updateConversationRecords(prev, conversationId, records =>
				updateRecordReply(records, recordId, prevReply => {
					const nextReply = {
						...prevReply,
						type: prevReply.type || "msg",
						message: done ? finalText : prevReply.message,
						streaming: !done,
						...(done && fullMessage ? { fullMessage } : {}),
						...(agentIDaasId ? { agentIDaasId } : {}),
					};

					if (done) {
						delete nextReply._streamText;
					} else {
						nextReply._streamText = streamState.text;
					}

					return nextReply;
				})
			)
		);
		if (done) {
			setAssistantTyping(false);
			streamSourceRef.current.delete(recordId);
			streamingManager.remove(recordId);
		}
	};

	const handleContactUpdate = (message) => {
		const { contact: updatedContact } = message;
		if (updatedContact) {
			setContact(updatedContact);
		}
	};

	const updateAssignedAgents = (assignees) => {
		if (!Array.isArray(assignees) || assignees.length === 0) {
			console.warn("updateAssignedAgents: No valid assignees, skipping update.");
			return;
		}
		const updatedAgents = assignees
			.map((assigneeId) => agentsRef.current.find((agent) => agent.agentIDaasId === assigneeId))
			.filter(Boolean);
		setAssignedAgentsState(updatedAgents);
	};

	const handleConvAssignee = (message) => {
		const { assignees, newConvId, prevConvId } = message;
		if (!prevConvId || !newConvId) {
			console.error("handleConvAssignee: Invalid conversation IDs", message);
			return;
		}
		if (Array.isArray(assignees)) {
			updateAssignedAgents(assignees);
		}
		setConversations((prevConversations) =>
			prevConversations.map((conversation) => {
				if (conversation._id === prevConvId) {
					return {
						...conversation,
						_id: newConvId,
						records: (conversation.records || []).map(record => ({
							...record,
							conversationId: newConvId
						}))
					};
				}
				return conversation;
			})
		);
		if (
			pagePropsRef.current?.location === "chat" &&
			pagePropsRef.current?.conversationId === prevConvId
		) {
			const updatedAgents = assignees
				.map((assigneeId) => agentsRef.current?.find((agent) => agent.agentIDaasId === assigneeId))
				.filter(Boolean);

			const updatedPage = { ...pagePropsRef.current, conversationId: newConvId, assignedAgents: updatedAgents };

			pagePropsRef.current = updatedPage;
			setAssignedAgentsState(updatedAgents);

			if (handleNavigate) {
				handleNavigate({ conversationId: newConvId, assignedAgents: updatedAgents });
			}
		}
	};

	const scrollBottom = (smooth = true) => {
		if (!messagesRef.current) return;
		messagesRef.current.scrollTo({
			top: messagesRef.current.scrollHeight,
			behavior: smooth ? "smooth" : "auto",
		});
	};

	const resolveMessage = (reply) => {
		if (!reply) return "";

		return (
			reply._streamText ??
			reply.message ??
			reply.config?.message ??
			""
		);
	};

	useEffect(() => {
		pagePropsRef.current = { ...pagePropsRef.current, conversationId, assignedAgents: initialAssignedAgents || [] };
	}, [conversationId, initialAssignedAgents]);

	useEffect(() => {
		if (conversations && pagePropsRef.current) {
			if (pagePropsRef.current.conversationId) {
				const conversation = conversations.find(conv => conv._id === pagePropsRef.current.conversationId);
				setConversation(conversation);
				const _records = conversation?.records || [];
				setRecords(_records);
				if (conversation?.placeholder === true && !initialized) {
					initSessionAndGetContact(_records);
					setInitialized(true);
				}
			} else {
				setConversation(null);
				const existingPlaceholder = conversations.find(conv => conv.placeholder === true && conv.status !== "Resolved");
				if (existingPlaceholder) {
					const _records = existingPlaceholder?.records || [];
					if (Array.isArray(_records) && _records.length === 0) {
						initSessionAndGetContact([]);
					} else {
						setRecords(_records);
					}
				} else if (!initialized) {
					initSessionAndGetContact([]);
				}
				setInitialized(true);
			}
		}
	}, [conversations, pagePropsRef]);

	useEffect(() => {
		if (agents) {
			agentsRef.current = agents;
			const conversation = conversations?.find((conv) => conv._id === pagePropsRef.current?.conversationId);
			if (conversation?.assignees) {
				updateAssignedAgents(conversation.assignees);
			} else if (pagePropsRef.current?.assignedAgents?.length) {
				setAssignedAgentsState(pagePropsRef.current.assignedAgents);
			}
		}
	}, [agents, conversations, pagePropsRef]);

	useEffect(() => {
		if (handleNavigate && pagePropsRef.current?.conversationId) {
			handleNavigate({ conversationId: pagePropsRef.current.conversationId, assignedAgents: assignedAgentsState });
		}
	}, [assignedAgentsState, handleNavigate]);

	useEffect(() => {
		if (!dataCollector) return;
		const nextAttr = dataCollector.innerAttrs.find((attr) => !attr.value);
		if (nextAttr && nextAttr !== dataCollector.currAttr) {
			setDataCollector((prev) => ({
				...prev,
				currAttr: nextAttr,
			}));
		} else if (!nextAttr) {
			const content = dataCollector.content;
			handleRequest({
				conversationId: pagePropsRef.current?.conversationId,
				action_query: {
					actionId: content?.actionId,
					intent: content.config.intent,
					meta: content,
					data: {
						attrs: dataCollector.innerAttrs
					}
				},
			});
			setDataCollector(null);
		}
	}, [dataCollector]);

	useEffect(() => {
		if (!contactDataCollector) return;
		const remainingAttrs = contactDataCollector.innerAttrs.filter(
			(attr) => !(attr.field in (contact?.meta || {})) || contact.meta[attr.field] === ""
		);
		const nextAttr = remainingAttrs.find((attr) => !attr.value);

		if (nextAttr && nextAttr !== contactDataCollector.currAttr) {
			setContactDataCollector((prev) => ({
				...prev,
				currAttr: nextAttr,
				innerAttrs: remainingAttrs,
			}));
		} else if (!nextAttr) {
			setContactDataCollector(null);
		}
	}, [contactDataCollector, contact?.meta]);

	useEffect(() => scrollBottom(true), [contactDataCollector, dataCollector]);

	useEffect(() => {
		if (!records || records.length === 0) return;
		scrollBottom(true);
	}, [records]);

	useEffect(() => {
		if (fieldError) {
			scrollBottom(true);
		}
	}, [fieldError]);

	useEffect(() => {
		if (assistantTyping) {
			scrollBottom(true);
		}
	}, [assistantTyping]);

	useEffect(() => {
		const handleSocketReconnected = async () => {
			const rawContactId = SessionManager.getSocketContactId()?.replace(/(_[^_]+)\1$/, '$1');
			if (!rawContactId) return;
			try {
				const res = await axiosUCS.get(`/conversations/all/${rawContactId}`, { params: { tenantId, botId } });
				const rawData = res.data?.data;
				const freshConversations = rawData?.conversations || (Array.isArray(rawData) ? rawData : []);
				if (freshConversations.length === 0) return;

				const openConversations = freshConversations.filter(c => c.status === "Open");
				const latest = [...openConversations].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
				if (latest && latest._id !== pagePropsRef.current?.conversationId) {
					handleConvAssignee({
						prevConvId: pagePropsRef.current?.conversationId,
						newConvId: latest._id,
						assignees: latest.assignees || [],
					});
				}

				const activeConvId = pagePropsRef.current?.conversationId;
				const serverConv = freshConversations.find(c => c._id === activeConvId);
				if (serverConv?.records?.length) {
					setConversations(prev =>
						updateConversationRecords(prev, activeConvId, localRecords => {
							const localMap = new Map(localRecords.map(r => [r._id, r]));
							let changed = false;
							const merged = localRecords.map(lr => {
								const sr = serverConv.records.find(s => s._id === lr._id);
								if (!sr || lr.streaming) return lr;
								if (!lr.reply && sr.reply) { changed = true; return sr; }
								return lr;
							});
							const missed = serverConv.records.filter(sr => !localMap.has(sr._id));
							if (missed.length > 0) changed = true;
							return changed ? [...merged, ...missed] : localRecords;
						})
					);
				}
			} catch (e) {
				console.warn("@@ Chat @@ Sync after reconnect failed", e);
			}
		};

		const listeners = [
			["assistant_typing", handleAssistantTyping],
			["record_update", handleRecordUpdate],
			["conversation_assignee", handleConvAssignee],
			["contact_update", handleContactUpdate],
			["record_update_stream", handleRecordUpdateStream],
			["socket_reconnected", handleSocketReconnected],
		];

		listeners.forEach(([event, handler]) => {
			eventEmitter.on(event, handler);
		});

		return () => {
			listeners.forEach(([event, handler]) => {
				eventEmitter.off(event, handler);
			});
		};
	}, []);

	return (
		<Box
			className="modern-chat-container"
			onDragOver={handleChatDragOver}
			onDragLeave={handleChatDragLeave}
			onDrop={handleChatDrop}
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				overflow: "hidden",
				position: "relative",
				"& .sender-ui > .sender-message-container": {
					background: "black !important",
				},
				"& .markdown-message-box": {
					background: "white !important",
					boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.10) !important",
				},
			}}
		>
			{isDragOverChat && (
				<Box
					sx={{
						position: "absolute",
						inset: 0,
						zIndex: 100,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "rgba(178, 110, 252, 0.08)",
						border: "2px dashed #B26EFC",
						borderRadius: "12px",
						pointerEvents: "none",
					}}
				>
					<Typography sx={{ color: "#B26EFC", fontWeight: 500, fontSize: "14px" }}>
						{msg("file.dragAndDrop", "Drop files here")}
					</Typography>
				</Box>
			)}

			{conversation && ["Telegram", "Whatsapp", "Messenger", "Viber"].includes(conversation.origin) ? (
				<Alert severity="warning">
					This conversation is from {conversation.origin}. Please use {conversation.origin} Messenger to reply to this conversation.
				</Alert>
			) : (
				<>
					<Box
						ref={messagesRef}
						sx={{
							flex: 1,
							overflowY: "auto",
							padding: "20px 20px 10px 20px",
							background: "white",
							display: "flex",
							flexDirection: "column",
							gap: 2,
						}}
					>
						{records && records.map((record) => (
							<Box key={record._id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								{record.query && (
									<Sender content={record.query} files={record.files} />
								)}
								{record.reply && (
									<Receiver
										content={record.reply}
										agents={agents}
										conversationId={pagePropsRef.current?.conversationId}
										contact={contact}
										contactDataCollector={contactDataCollector}
										dataCollector={dataCollector}
										recordId={record._id}
										handleRequest={handleRequest}
										handleNavigate={handleNavigate}
										setSelectedDocument={setSelectedDocument}
										setSelectedCollection={setSelectedCollection}
										widget={widget}
										resolveMessage={resolveMessage}
										{...props}
									/>
								)}
								{record.correlationId && (
									<Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
										<Tooltip title={copiedRef === record.correlationId ? msg("chat.ref_copied", "Reference copied") : msg("chat.copy_ref", "Copy reference ID")}>
											<IconButton
												size="small"
												onClick={() => copyRef(record.correlationId)}
												sx={{ color: "#C0C0C0", p: 0.25, "&:hover": { color: "#B26EFC" } }}
											>
												<CopyIcon sx={{ fontSize: 13 }} />
											</IconButton>
										</Tooltip>
									</Box>
								)}
							</Box>
						))}
						{assistantTyping && <TypingIndicator />}
						{fieldError && (
							<Typography variant="caption" sx={{ color: "#d32f2f", fontSize: "12px" }}>
								{fieldError}
							</Typography>
						)}
						{sendError && (
							<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
								<Typography variant="caption" sx={{ color: "#d32f2f", fontSize: "12px" }}>
									{msg("chat.send_error", "Something went wrong. Reference: {id}", { id: sendError.id })}
								</Typography>
								<Tooltip title={copiedRef === sendError.id ? msg("chat.ref_copied", "Reference copied") : msg("chat.copy_ref", "Copy reference ID")}>
									<IconButton size="small" onClick={() => copyRef(sendError.id)} sx={{ color: "#d32f2f", p: 0.25 }}>
										<CopyIcon sx={{ fontSize: 13 }} />
									</IconButton>
								</Tooltip>
							</Box>
						)}
					</Box>

					<Box sx={{ padding: "12px 20px" }}>
						{isResolved ? (
							<Box sx={{ textAlign: "center", padding: 2, color: "#888", fontSize: "14px" }}>
								{msg("conversations.ended_conversation", "This conversation has ended, please start a new conversation.")}
							</Box>
						) : (
							<>
								{attachedFiles.length > 0 && (
									<AttachmentTray
										files={attachedFiles}
										onRemove={handleRemoveFile}
										onDrop={handleDrop}
										msg={msg}
										disabled={!!(dataCollector || contactDataCollector)}
									/>
								)}
								<TextField
									fullWidth
									multiline
									minRows={1}
									maxRows={4}
									inputRef={inputRef}
									placeholder={
										dataCollector?.currAttr?.name ||
										contactDataCollector?.currAttr?.name ||
										msg("conversations.input_placeholder", "Ask me anything")
									}
									value={query}
									onChange={(e) => setQuery(e.target.value)}
									onKeyPress={(e) => {
										if (e.key === "Enter" && !e.shiftKey) {
											e.preventDefault();
											handleSubmit();
										}
									}}
									size="small"
									sx={{
										"& .MuiOutlinedInput-root": {
											borderRadius: "16px",
											background: "white",
											border: "1px solid #E8E8E8",
											"& fieldset": {
												border: "none",
											},
										},
									}}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<IconButton
													size="small"
													sx={{ color: "#808080" }}
													onClick={() => fileInputRef.current?.click()}
													disabled={!!(dataCollector || contactDataCollector) || attachedFiles.length >= MAX_FILES}
												>
													<AttachIcon fontSize="small" />
												</IconButton>
												<input
													ref={fileInputRef}
													type="file"
													multiple
													accept={ALLOWED_MIME_TYPES.join(",")}
													style={{ display: "none" }}
													onChange={handleFileInputChange}
												/>
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													size="small"
													onClick={handleSubmit}
													disabled={!canSend}
													sx={{ color: canSend ? "#B26EFC" : "#808080" }}
												>
													<SendIcon fontSize="small" />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</>
						)}
					</Box>
				</>
			)}
		</Box>
	);
}
